import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { AreaGuard } from "@/components/auth/AreaGuard";
import { PlatformGuard } from "@/components/auth/PlatformGuard";
import { ChatLayout } from "@/components/chrome/ChatLayout";
import { AdminLayout } from "@/components/chrome/AdminLayout";
import { PlatformLayout } from "@/components/chrome/PlatformLayout";
import { DevLayout } from "@/components/chrome/DevLayout";
import { CrmLayout } from "@/components/chrome/CrmLayout";
import { ErrorBoundary } from "@/components/feedback/ErrorBoundary";
import { DockProvider } from "@/components/command/DockContext";
import { useMyAccess } from "@/hooks/useMyAccess";
import {
  ADMIN_ROUTE_ENTITLEMENTS,
  ADMIN_ROUTE_SECTIONS,
  ADMIN_SECTION_KEYS,
} from "@/lib/areas/registry";

// Existing pages
const HomePage = lazy(() => import("@/pages/HomePage"));
const ResultsPage = lazy(() => import("@/pages/ResultsPage"));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const BookingsPage = lazy(() => import("@/pages/BookingsPage"));
const BookingDetailPage = lazy(() => import("@/pages/BookingDetailPage"));
const BookingGroupDetailPage = lazy(
  () => import("@/pages/BookingGroupDetailPage"),
);
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Ops top-level
const AnalyticsPage = lazy(() => import("@/pages/ops/AnalyticsPage"));
const CalendarPage = lazy(() => import("@/pages/ops/CalendarPage"));
const AgencyPage = lazy(() => import("@/pages/ops/AgencyPage"));
const DistributionConnectionPage = lazy(
  () => import("@/pages/ops/DistributionConnectionPage"),
);
const SmartHelperPage = lazy(() => import("@/pages/ops/SmartHelperPage"));
const SupplierConnectorPage = lazy(
  () => import("@/pages/ops/SupplierConnectorPage"),
);
const SystemIntegrationPage = lazy(
  () => import("@/pages/ops/SystemIntegrationPage"),
);
const ProfilePage = lazy(() => import("@/pages/ops/ProfilePage"));

// Admin
const AdminLandingPage = lazy(() => import("@/pages/admin/AdminLandingPage"));
const BankingPage = lazy(() => import("@/pages/admin/BankingPage"));
const AdminCalendarPage = lazy(() => import("@/pages/admin/AdminCalendarPage"));
const CatalogPage = lazy(() => import("@/pages/admin/CatalogPage"));
const CommissionsPage = lazy(() => import("@/pages/admin/CommissionsPage"));
const ContractRoomPage = lazy(() => import("@/pages/admin/ContractRoomPage"));
const FinancePage = lazy(() => import("@/pages/admin/FinancePage"));
const LibriGiornaliPage = lazy(() => import("@/pages/admin/LibriGiornaliPage"));
const PayrollPage = lazy(() => import("@/pages/admin/PayrollPage"));
const PrimaNotaPage = lazy(() => import("@/pages/admin/PrimaNotaPage"));
const ProjectsPage = lazy(() => import("@/pages/admin/ProjectsPage"));
const RatingPage = lazy(() => import("@/pages/admin/RatingPage"));
const RiconciliazioniPage = lazy(
  () => import("@/pages/admin/RiconciliazioniPage"),
);
const TasksPage = lazy(() => import("@/pages/admin/TasksPage"));
const TeamPage = lazy(() => import("@/pages/admin/TeamPage"));
const WalletPage = lazy(() => import("@/pages/admin/WalletPage"));
const SyncCenterPage = lazy(() => import("@/pages/admin/SyncCenterPage"));
const AvesSyncPage = lazy(() => import("@/pages/admin/AvesSyncPage"));
const AvesSyncWizardPage = lazy(() => import("@/pages/admin/AvesSyncWizardPage"));
const AlertsPage = lazy(() => import("@/pages/admin/AlertsPage"));
const DirectoryPage = lazy(() => import("@/pages/admin/DirectoryPage"));
const PartyDetailPage = lazy(() => import("@/pages/admin/PartyDetailPage"));
const RateSheetWizardPage = lazy(() => import("@/pages/admin/RateSheetWizardPage"));
const RateSheetPage = lazy(() => import("@/pages/admin/RateSheetPage"));
const PracticesPage = lazy(() => import("@/pages/admin/PracticesPage"));
const PracticeDetailPage = lazy(() => import("@/pages/admin/PracticeDetailPage"));
const PlatformFeaturesPage = lazy(() => import("@/pages/platform/PlatformFeaturesPage"));
const PlatformEmailSetupPage = lazy(() => import("@/pages/platform/PlatformEmailSetupPage"));
const PlatformUsersPage = lazy(() => import("@/pages/platform/PlatformUsersPage"));
const PlatformOrgsPage = lazy(() => import("@/pages/platform/PlatformOrgsPage"));
const PlatformOrgWizardPage = lazy(() => import("@/pages/platform/PlatformOrgWizardPage"));
const PlatformOrgDetailPage = lazy(() => import("@/pages/platform/PlatformOrgDetailPage"));

// Dev
const DevConsolePage = lazy(() => import("@/pages/dev/DevConsolePage"));
const AutoConnectorPage = lazy(() => import("@/pages/dev/AutoConnectorPage"));
const AiMapperPage = lazy(() => import("@/pages/dev/AiMapperPage"));
const RuntimePage = lazy(() => import("@/pages/dev/RuntimePage"));
const RuntimeChoicePage = lazy(() => import("@/pages/dev/RuntimeChoicePage"));
const SupplierRuntimePage = lazy(() => import("@/pages/dev/SupplierRuntimePage"));

// CRM
const CrmComingSoonPage = lazy(() => import("@/pages/crm/CrmComingSoonPage"));

function PageFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-muted">
      <Loader2 className="w-5 h-5 animate-spin text-accent" />
    </div>
  );
}

function PageWrap({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

// /admin child route: section-gated via the ADMIN_ROUTE_SECTIONS single
// source (registry.ts). Sections are fail-open (old BE / legacy users see
// everything), so this changes nothing for existing orgs. Entitlement-gating
// (ADMIN_ROUTE_ENTITLEMENTS) is instead fail-closed on the mapped routes:
// org entitlement AND user access, uniformly for Aves/Pricing/Practices/etc.
function AdminPage({ path, children }: { path: string; children: React.ReactNode }) {
  return (
    <AreaGuard
      area="admin"
      section={ADMIN_ROUTE_SECTIONS[path]}
      entitlement={ADMIN_ROUTE_ENTITLEMENTS[path]}
    >
      <PageWrap>{children}</PageWrap>
    </AreaGuard>
  );
}

// /admin index: a sync-only operator (allowed sections within sync/alerts)
// lands straight on the Sync Center instead of the (empty) landing.
function AdminIndexGate({ children }: { children: React.ReactNode }) {
  const { canSection, isLoading } = useMyAccess();
  if (isLoading) return null;
  const allowed = ADMIN_SECTION_KEYS.filter((k) => canSection("admin", k));
  const syncOnly =
    allowed.includes("sync") && allowed.every((k) => k === "sync" || k === "alerts");
  if (syncOnly) return <Navigate to="/admin/sync-center" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  {
    path: "/onboarding",
    element: (
      <ProtectedRoute requireOnboarding={false}>
        <PageWrap>
          <OnboardingPage />
        </PageWrap>
      </ProtectedRoute>
    ),
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <ChatLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PageWrap><HomePage /></PageWrap> },
      { path: "results/:searchId", element: <PageWrap><ResultsPage /></PageWrap> },
      { path: "booking", element: <PageWrap><BookingPage /></PageWrap> },
      { path: "bookings", element: <PageWrap><BookingsPage /></PageWrap> },
      { path: "bookings/:id", element: <PageWrap><BookingDetailPage /></PageWrap> },
      { path: "bookings-groups/:groupId", element: <PageWrap><BookingGroupDetailPage /></PageWrap> },
      { path: "settings", element: <PageWrap><SettingsPage /></PageWrap> },
      // Ops top-level
      { path: "analytics", element: <PageWrap><AnalyticsPage /></PageWrap> },
      { path: "calendar", element: <PageWrap><CalendarPage /></PageWrap> },
      { path: "agency", element: <PageWrap><AgencyPage /></PageWrap> },
      { path: "distribution-connection", element: <PageWrap><DistributionConnectionPage /></PageWrap> },
      { path: "smart-helper", element: <PageWrap><SmartHelperPage /></PageWrap> },
      { path: "supplier-connector", element: <PageWrap><SupplierConnectorPage /></PageWrap> },
      { path: "system-integration", element: <PageWrap><SystemIntegrationPage /></PageWrap> },
      { path: "profile", element: <PageWrap><ProfilePage /></PageWrap> },
    ],
  },
  {
    path: "/admin",
    element: (
      <ProtectedRoute>
        <AreaGuard area="admin">
          <AdminLayout />
        </AreaGuard>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <AdminIndexGate><PageWrap><AdminLandingPage /></PageWrap></AdminIndexGate> },
      { path: "banking", element: <AdminPage path="banking"><BankingPage /></AdminPage> },
      { path: "calendar", element: <AdminPage path="calendar"><AdminCalendarPage /></AdminPage> },
      { path: "catalog", element: <AdminPage path="catalog"><CatalogPage /></AdminPage> },
      { path: "commissions", element: <AdminPage path="commissions"><CommissionsPage /></AdminPage> },
      { path: "contract-room", element: <AdminPage path="contract-room"><ContractRoomPage /></AdminPage> },
      { path: "finance", element: <AdminPage path="finance"><FinancePage /></AdminPage> },
      { path: "libri-giornali", element: <AdminPage path="libri-giornali"><LibriGiornaliPage /></AdminPage> },
      { path: "payroll", element: <AdminPage path="payroll"><PayrollPage /></AdminPage> },
      { path: "prima-nota", element: <AdminPage path="prima-nota"><PrimaNotaPage /></AdminPage> },
      { path: "projects", element: <AdminPage path="projects"><ProjectsPage /></AdminPage> },
      { path: "rating", element: <AdminPage path="rating"><RatingPage /></AdminPage> },
      { path: "riconciliazioni", element: <AdminPage path="riconciliazioni"><RiconciliazioniPage /></AdminPage> },
      { path: "tasks", element: <AdminPage path="tasks"><TasksPage /></AdminPage> },
      { path: "team", element: <AdminPage path="team"><TeamPage /></AdminPage> },
      { path: "wallet", element: <AdminPage path="wallet"><WalletPage /></AdminPage> },
      { path: "sync-center", element: <AdminPage path="sync-center"><SyncCenterPage /></AdminPage> },
      { path: "sync-center/aves", element: <AdminPage path="sync-center/aves"><AvesSyncPage /></AdminPage> },
      { path: "sync-center/aves/new", element: <AdminPage path="sync-center/aves/new"><AvesSyncWizardPage /></AdminPage> },
      { path: "alerts", element: <AdminPage path="alerts"><AlertsPage /></AdminPage> },
      // I listini non hanno piu' una pagina propria: vivono dentro l'anagrafica
      // del fornitore/canale. Il vecchio link resta valido e ci porta.
      { path: "pricing", element: <Navigate to="/admin/directory" replace /> },
      { path: "directory", element: <AdminPage path="directory"><DirectoryPage /></AdminPage> },
      { path: "directory/:partyId",
        element: <AdminPage path="directory/:partyId"><PartyDetailPage /></AdminPage> },
      { path: "directory/:partyId/listini/nuovo",
        element: <AdminPage path="directory/:partyId/listini/nuovo"><RateSheetWizardPage /></AdminPage> },
      { path: "directory/:partyId/listini/:sheetId",
        element: <AdminPage path="directory/:partyId/listini/:sheetId"><RateSheetPage /></AdminPage> },
      { path: "practices", element: <AdminPage path="practices"><PracticesPage /></AdminPage> },
      { path: "practices/:practiceId",
        element: <AdminPage path="practices/:practiceId"><PracticeDetailPage /></AdminPage> },
    ],
  },
  {
    path: "/platform",
    element: (
      <ProtectedRoute>
        <PlatformGuard>
          <PlatformLayout />
        </PlatformGuard>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PageWrap><PlatformFeaturesPage /></PageWrap> },
      { path: "email-setup", element: <PageWrap><PlatformEmailSetupPage /></PageWrap> },
      { path: "users", element: <PageWrap><PlatformUsersPage /></PageWrap> },
      { path: "orgs", element: <PageWrap><PlatformOrgsPage /></PageWrap> },
      { path: "orgs/new", element: <PageWrap><PlatformOrgWizardPage /></PageWrap> },
      { path: "orgs/:orgId", element: <PageWrap><PlatformOrgDetailPage /></PageWrap> },
      { path: "orgs/:orgId/onboarding", element: <PageWrap><PlatformOrgWizardPage /></PageWrap> },
    ],
  },
  {
    path: "/dev",
    element: (
      <ProtectedRoute>
        <AreaGuard area="dev">
          <DevLayout />
        </AreaGuard>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PageWrap><DevConsolePage /></PageWrap> },
      { path: "auto-connector", element: <PageWrap><AutoConnectorPage /></PageWrap> },
      { path: "ai-mapper", element: <PageWrap><AiMapperPage /></PageWrap> },
      {
        path: "runtime",
        children: [
          { index: true, element: <PageWrap><RuntimeChoicePage /></PageWrap> },
          { path: "ota", element: <PageWrap><RuntimePage /></PageWrap> },
          { path: "supplier", element: <PageWrap><SupplierRuntimePage /></PageWrap> },
        ],
      },
    ],
  },
  {
    path: "/crm",
    element: (
      <ProtectedRoute>
        <AreaGuard area="crm">
          <CrmLayout />
        </AreaGuard>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <PageWrap><CrmComingSoonPage /></PageWrap> },
    ],
  },
  // Preview senza auth per ispezionare rendering pagine in locale senza dover
  // bootstrappare Keycloak. Mantiene lo stesso style system del layout reale.
  // Le route /dev|/admin protette restano primarie — queste sono shortcut.
  {
    path: "/_preview/ai-mapper",
    element: (
      <DockProvider>
        <div className="min-h-screen bg-[#0a0b14] text-white">
          <PageWrap><AiMapperPage /></PageWrap>
        </div>
      </DockProvider>
    ),
  },
  {
    path: "/_preview/dev-console",
    element: (
      <DockProvider>
        <div className="min-h-screen bg-[#0a0b14] text-white">
          <PageWrap><DevConsolePage /></PageWrap>
        </div>
      </DockProvider>
    ),
  },
  {
    path: "/_preview/auto-connector",
    element: (
      <DockProvider>
        <div className="min-h-screen bg-[#0a0b14] text-white">
          <PageWrap><AutoConnectorPage /></PageWrap>
        </div>
      </DockProvider>
    ),
  },
  {
    path: "/_preview/runtime",
    element: (
      <DockProvider>
        <div className="min-h-screen bg-[#0a0b14] text-white">
          <PageWrap><RuntimeChoicePage /></PageWrap>
        </div>
      </DockProvider>
    ),
  },
  {
    path: "/_preview/runtime/ota",
    element: (
      <DockProvider>
        <div className="min-h-screen bg-[#0a0b14] text-white">
          <PageWrap><RuntimePage /></PageWrap>
        </div>
      </DockProvider>
    ),
  },
  {
    path: "/_preview/runtime/supplier",
    element: (
      <DockProvider>
        <div className="min-h-screen bg-[#0a0b14] text-white">
          <PageWrap><SupplierRuntimePage /></PageWrap>
        </div>
      </DockProvider>
    ),
  },
  {
    path: "/_preview/admin-landing",
    element: (
      <div className="min-h-screen bg-background text-foreground">
        <PageWrap><AdminLandingPage /></PageWrap>
      </div>
    ),
  },
  {
    path: "/_preview/admin-calendar",
    element: (
      <div className="min-h-screen bg-background text-foreground">
        <PageWrap><AdminCalendarPage /></PageWrap>
      </div>
    ),
  },
  {
    path: "/_preview/admin-shell",
    element: <AdminLayout />,
    children: [
      { index: true, element: <PageWrap><SyncCenterPage /></PageWrap> },
      { path: "alerts", element: <PageWrap><AlertsPage /></PageWrap> },
      { path: "sync-center", element: <PageWrap><SyncCenterPage /></PageWrap> },
    ],
  },
  {
    path: "/_preview/admin-sync-center",
    element: (
      <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-indigo-50 via-white to-cyan-50 text-foreground">
        <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_15%_-10%,rgba(99,102,241,0.10),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(60%_45%_at_100%_100%,rgba(6,182,212,0.10),transparent_55%)]" />
        </div>
        <div className="relative z-10"><PageWrap><SyncCenterPage /></PageWrap></div>
      </div>
    ),
  },
  {
    path: "/_preview/admin-alerts",
    element: (
      <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-indigo-50 via-white to-cyan-50 text-foreground">
        <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_15%_-10%,rgba(99,102,241,0.10),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(60%_45%_at_100%_100%,rgba(6,182,212,0.10),transparent_55%)]" />
        </div>
        <div className="relative z-10"><PageWrap><AlertsPage /></PageWrap></div>
      </div>
    ),
  },
  {
    path: "/_preview/home",
    element: (
      <div className="min-h-screen bg-background text-foreground">
        <PageWrap><HomePage /></PageWrap>
      </div>
    ),
  },
  {
    path: "/_preview/booking",
    element: (
      <div className="min-h-screen bg-background text-foreground">
        <PageWrap><BookingPage /></PageWrap>
      </div>
    ),
  },
  { path: "*", element: <Navigate to="/" replace /> },
  { path: "/404", element: <PageWrap><NotFoundPage /></PageWrap> },
]);
